import React, { useRef } from 'react';
import { Typography, Box, Tooltip } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { getFolderName } from '../../../../utils/getFolderName';
import { FileActionsMenu } from '../../modules/FileActionsMenu';
import { InlineRenameField, hasAction, useInlineRename } from '../../modules/InlineRename';
import { AccessMark, FolderGlyph, useFolderLinkText } from '../../modules/FolderGlyph';

// `link`: how Orbit manages the folder (getFolderLink), shown in its icon
export const FolderCard = ({ folder, link, onClick, dragData, isOver: isOverProp, canDrop, actions, onAction, onRename }) => {
  const linkText = useFolderLinkText(link);
  const cardRef = useRef(null);
  const { renaming, stopRenaming, nameProps, rowKeyDown } = useInlineRename({
    enabled: Boolean(onRename) && hasAction(actions, 'rename'),
    onOpen: () => onClick?.(folder),
  });
  const folderName = getFolderName(folder.folder_name);
  const dropId = `folder:${folder.folder_id}`;

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable(
    dragData ?? { id: `drag-disabled-${folder.folder_id}`, disabled: true }
  );

  const { setNodeRef: setDropRef, isOver: isOverDrop } = useDroppable({
    id: dropId,
    // A folder its access rules leave read-only takes no drops
    disabled: !canDrop || folder.can_write === false,
  });

  const isOver = isOverProp || isOverDrop;
  // See FolderRow: no drag attributes (aria-disabled) when it can't be dragged
  const draggable = Boolean(dragData && !dragData.disabled);

  const setRef = (el) => {
    setDragRef(el);
    setDropRef(el);
    cardRef.current = el;
  };

  return (
    <Box
      ref={setRef}
      {...(draggable ? { ...listeners, ...attributes } : {})}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || rowKeyDown(e)) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(folder, e);
        }
      }}
      sx={{
        position: 'relative',
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        '&:hover .folder-card-actions, &:focus-within .folder-card-actions': { opacity: 1 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: isDragging ? 'grabbing' : 'pointer',
        transition: 'all 0.2s',
        p: 1,
        borderRadius: 1,
        opacity: isDragging ? 0.4 : 1,
        outline: isOver ? '2px solid' : 'none',
        outlineColor: 'primary.main',
        backgroundColor: isOver ? 'action.selected' : 'transparent',
        '&:hover .folder-icon': { opacity: 0.7 },
      }}
      // See FolderRow: skip the second click of a double-click
      onClick={(e) => { if (e.detail <= 1) onClick?.(folder, e); }}
    >
      {actions?.length > 0 && (
        <Box
          className="folder-card-actions"
          sx={{ position: 'absolute', top: 2, right: 2, zIndex: 1, opacity: 0, transition: 'opacity 0.15s', bgcolor: 'background.paper', borderRadius: '50%' }}
        >
          <FileActionsMenu file={folder} actions={actions} onAction={onAction} />
        </Box>
      )}
      <Box
        sx={{
          width: 80,
          height: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1,
        }}
      >
        <Tooltip title={linkText?.hint || ''}>
          <span className="folder-icon" style={{ display: 'inline-flex', transition: 'opacity 0.2s' }}>
            <FolderGlyph link={link} size={64} />
          </span>
        </Tooltip>
      </Box>

      {renaming ? (
        <InlineRenameField
          name={folder.folder_name}
          onSave={(newName) => onRename(folder, newName)}
          onClose={({ keyboard }) => {
            stopRenaming();
            if (keyboard) cardRef.current?.focus();
          }}
          sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem' } }}
        />
      ) : (
        <Tooltip title={folderName} placement="top">
          <Typography
            variant="caption"
            {...nameProps}
            sx={{
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <AccessMark item={folder} sx={{ mr: 0.5, verticalAlign: 'middle' }} />
            {folderName}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};
